import { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import PopoverTreeSelector from 'components/common/popover-tree-selector/popover-tree-selector';
import { ExpansionMode } from 'components/common/popover-tree-selector/use-popover-tree-expansion';
import CreateProjectModal from 'components/projects/create-project-modal';
import { intl } from 'components/localization';
import values from 'lodash/values';
import styled from 'types/theme.types';
import { useNavigate } from 'react-router-dom';
import { useCuratedProjects } from 'stores/rq/projects';
import { useCurrentProjectId } from 'hooks/path-params-extraction.hooks';
import type { Project } from 'types/api.types';
import type { TreeDataNode } from 'antd';
import { useHasPermission } from 'hooks/use-has-permission';
import { FolderOpenOutlined } from '@ant-design/icons';
import PopoverTreeLabel from 'components/layout/topbar/popover-tree-label';
import useRecentlyVisitedProjects from 'hooks/use-recently-visited-projects.hook';
import TopbarProjectSelectorRecentProjects from 'components/layout/topbar/topbar-project-selector-recent-projects';
import { getHierarchySegments } from 'utils/sub-project.utils';

const POPUP_WIDTH_PX = 347;

const TopBarProjectSelector = () => {
  const navigate = useNavigate();
  const currentProjectId = useCurrentProjectId();
  const { projects, activeRootProjects, isLoading } = useCuratedProjects();
  const recentProjectIds = useRecentlyVisitedProjects();
  const [parentProjectId, setParentProjectId] = useState<string | null>(null);
  const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);
  const { isAuthorized: canCreateProject } = useHasPermission('CREATE_PROJECT', { skipProjectCheck: true });
  const handleProjectSelect = useCallback(
    (selectedKeys: React.Key[]) => {
      const projectId = selectedKeys[0] as string;
      if (projectId && projectId !== currentProjectId) {
        navigate(`/p/${projectId}/environments`);
      }
    },
    [navigate, currentProjectId]
  );

  const getProjectUrl = useCallback((projectId: React.Key) => {
    return `/p/${projectId}/environments`;
  }, []);

  const handleAddProject = (projectId: React.Key) => {
    setParentProjectId(projectId as string);
    setIsAddProjectModalOpen(true);
  };

  const handleFooterAddProject = () => {
    // When clicking the footer button, we're adding a root project (no parent)
    setParentProjectId(null);
    setIsAddProjectModalOpen(true);
  };

  const buildTreeData = useCallback((projects: Project[]): TreeDataNode[] => {
    return projects
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(project => {
        const hasChildren = project.children && project.children.length > 0;

        return {
          key: project.id,
          title: <ProjectOption>{project.name}</ProjectOption>,
          searchText: project.name,
          children: hasChildren ? buildTreeData(project.children) : undefined,
          selectable: true,
          disabled: !project.isReadable,
          canCreate: project.canCreateSubProject
        };
      });
  }, []);

  const treeData = useMemo(() => buildTreeData(values(activeRootProjects)), [activeRootProjects, buildTreeData]);

  const currentProject = currentProjectId ? projects[currentProjectId] : undefined;

  const recentItems = useMemo(() => {
    const validProjects = (recentProjectIds || []).filter(id => !!projects[id]);

    return validProjects.map(id => {
      const project = projects[id];
      const { segments } = getHierarchySegments(id, projects);

      return { id, name: project.name, segments };
    });
  }, [recentProjectIds, projects]);

  const onAddProjectModalHide = () => {
    setIsAddProjectModalOpen(false);
    setParentProjectId(null);
  };

  return (
    <>
      <PopoverTreeSelector
        treeData={treeData}
        selectedKeys={currentProjectId ? [currentProjectId] : []}
        onSelect={handleProjectSelect}
        trigger={
          <PopoverTreeLabel
            icon={<ProjectIcon />}
            text={currentProject?.name || intl.formatMessage({ id: 'topbar.project-selector.label' })}
            data-e2e="project-label"
          />
        }
        showSearch={true}
        searchPlaceholder={intl.formatMessage({ id: 'topbar.project-selector.search-placeholder' })}
        onAddClick={handleAddProject}
        footerTitle={canCreateProject ? intl.formatMessage({ id: 'topbar.project-selector.add-button' }) : undefined}
        onFooterClick={canCreateProject ? handleFooterAddProject : undefined}
        isLoading={isLoading}
        dataE2e="project-popover-tree-selector"
        getNodeUrl={getProjectUrl}
        popoverWidthPx={POPUP_WIDTH_PX}
        headerContent={(close: () => void) => (
          <TopbarProjectSelectorRecentProjects
            recentItems={recentItems}
            currentProjectId={currentProjectId}
            onClose={close}
            maxWidthPx={POPUP_WIDTH_PX}
          />
        )}
        expansionMode={ExpansionMode.EXPAND_ROOT_NODES}
      />
      <CreateProjectModal
        open={isAddProjectModalOpen}
        hideModal={onAddProjectModalHide}
        parentProjectId={parentProjectId || undefined}
      />
    </>
  );
};

const ProjectOption = styled.div`
  display: flex;
  align-items: center;
`;

const ProjectIcon = styled(FolderOpenOutlined)`
  font-size: 13px;
`;

export default observer(TopBarProjectSelector);
